import React, { useState } from 'react';

// Custom SVG Icons
const DownloadIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const DescriptionIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
  </svg>
);

const AttachFileIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

const VisibilityIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
  </svg>
);

const VisibilityOffIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
  </svg>
);

const CloseIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const TroubleshootingDetails = ({ ticket, formatDate, handleDownload }) => {
  const [showPreview, setShowPreview] = useState(false);

  // Get file details from either documentPath or Google Drive
  const getFileDetails = () => {
    // First check for Google Drive file details
    if (ticket.driveFileUrl) {
      return {
        url: ticket.driveFileUrl,
        name: ticket.driveFileName || 'Attached Document',
        isGoogleDrive: true
      };
    }
    // Then check for legacy file paths
    if (ticket.documentPath) {
      return {
        path: ticket.documentPath,
        name: ticket.documentPath.split('/').pop(),
        isGoogleDrive: false
      };
    }
    if (ticket.categorySpecificDetails?.details?.fileUrl) {
      return {
        url: ticket.categorySpecificDetails.details.fileUrl,
        name: ticket.categorySpecificDetails.details.fileName || 'Attached Document',
        isGoogleDrive: true
      };
    }
    return null;
  };

  // Function to get the embed URL from the Google Drive view URL
  const getEmbedUrl = (viewUrl) => {
    if (!viewUrl) return null;
    // Convert the view URL to an embed URL
    return viewUrl.replace('/view', '/preview');
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const fileDetails = getFileDetails();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-blue-600 mb-4 font-poppins">
          Technical Issue Details
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Name</div>
            <div className="text-gray-900 font-poppins">{ticket.name || 'N/A'}</div>
          </div>
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Email</div>
            <div className="text-gray-900 font-poppins">{ticket.email || 'N/A'}</div>
          </div>
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Location</div>
            <div className="text-gray-900 font-poppins">{ticket.location?.type === 'SCHOOL' ? 'School - Imus City' : 'SDO - Imus City'}</div>
          </div>
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Date of Request</div>
            <div className="text-gray-900 font-poppins">{ticket.dateOfRequest ? formatDate(ticket.dateOfRequest) : 'N/A'}</div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Type of Equipment</div>
            <div className="text-gray-900 font-poppins">{ticket.typeOfEquipment || 'N/A'}</div>
          </div>
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Model of Equipment</div>
            <div className="text-gray-900 font-poppins">{ticket.modelOfEquipment || 'N/A'}</div>
          </div>
          <div className="py-2">
            <div className="text-sm font-medium text-gray-600 mb-1 font-poppins">Serial No.</div>
            <div className="text-gray-900 font-poppins">{ticket.serialNo || 'N/A'}</div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <div className="py-2">
          <div className="text-sm font-medium text-gray-600 mb-2 font-poppins">Specific Problem</div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 text-gray-900 whitespace-pre-wrap font-poppins">
            {ticket.specificProblem || 'Not provided'}
          </div>
        </div>
      </div>
      {fileDetails && (
        <div className="mt-6">
          <div className="mt-4">
            <h4 className="text-lg font-semibold text-blue-600 mb-4 flex items-center gap-2 font-poppins">
              <AttachFileIcon className="w-5 h-5" />
              Attached File
            </h4>
            <div className="mt-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-poppins text-sm max-w-xs">
                  <DescriptionIcon className="w-4 h-4" />
                  {fileDetails.name}
                </span>
                {fileDetails.isGoogleDrive && (
                  <button
                    onClick={togglePreview}
                    className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {showPreview ? <VisibilityOffIcon className="w-5 h-5" /> : <VisibilityIcon className="w-5 h-5" />}
                  </button>
                )}
                <button
                  onClick={() => handleDownload(fileDetails.path || fileDetails.url)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-poppins text-sm"
                >
                  <DownloadIcon className="w-4 h-4" />
                  Download
                </button>
              </div>

              {/* File Preview Modal */}
              {fileDetails.isGoogleDrive && showPreview && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="relative w-full max-w-6xl max-h-[90vh] overflow-hidden bg-white rounded-lg border border-gray-200 shadow-xl">
                    <button
                      onClick={togglePreview}
                      className="absolute right-2 top-2 z-10 p-2 bg-white/80 hover:bg-white/90 rounded-lg transition-colors"
                    >
                      <CloseIcon className="w-5 h-5" />
                    </button>
                    <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
                      <iframe
                        src={getEmbedUrl(fileDetails.url)}
                        className="absolute top-0 left-0 w-full h-full border-0"
                        allow="autoplay"
                        allowFullScreen
                        title="Document Preview"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TroubleshootingDetails; 